import {BaseUser, UserType} from '@legit.health/ui';
import {redirect} from '@tanstack/react-router';
import {LOGIN} from '@/anonymous/routing/paths';
import {CurrentPractitioner} from '@/practitioner/interfaces/Practitioner';
import {COMPANY_UPDATE_PAYMENT_METHOD, INACTIVE_COMPANY} from '@/practitioner/routing/paths';
import isCurrentPractitioner from '@/practitioner/utils/isCurrentPractitioner';
import {CurrentUserBase} from '@/shared/contexts/UserContext';
import {AppRouterContext} from '@/shared/routing';
import {LOGOUT, USER_HOME} from '@/shared/routing/paths';

export const accessGranted = (user: CurrentUserBase) => isCurrentPractitioner(user);

export const companyAdminGranted = (user: CurrentUserBase) =>
  isCurrentPractitioner(user) && user.practitionerPermissions.administerCompany;

export const practitionerAdminGranted = (user: CurrentUserBase) =>
  isCurrentPractitioner(user) && user.practitionerPermissions.administerPractitioners;

export const viewPractitionersGranted = (user: CurrentUserBase) =>
  isCurrentPractitioner(user) && user.practitionerPermissions.viewPractitioners;

export const viewDiagnosticReportGranted = (user: CurrentUserBase) =>
  isCurrentPractitioner(user) && user.practitionerPermissions.viewDiagnosticReports;

export const workListAccessGranted = (user: CurrentUserBase) =>
  isCurrentPractitioner(user) && !user.isAdmissions;

function isPractitioner(user: BaseUser): user is CurrentPractitioner {
  return (user as CurrentPractitioner).type === UserType.Practitioner;
}

export const qualifiedPractitionerGranted = (user: CurrentUserBase) =>
  isCurrentPractitioner(user) && user.isQualifiedPractitioner;

export function grantUser(accessCheck: (user: CurrentPractitioner) => boolean) {
  return ({
    context: {company, currentUser},
    location: {pathname}
  }: {
    context: AppRouterContext;
    location: {pathname: string};
  }) => {
    if (!currentUser || !company) {
      throw redirect({to: LOGIN, replace: true});
    }
    if (!isPractitioner(currentUser)) {
      throw redirect({to: USER_HOME, replace: true});
    }
    if (
      !company.isActive &&
      company.license.hasStartedSubscription &&
      currentUser?.isAdmin &&
      pathname !== COMPANY_UPDATE_PAYMENT_METHOD &&
      pathname !== LOGOUT
    ) {
      throw redirect({to: COMPANY_UPDATE_PAYMENT_METHOD, replace: true});
    }
    if (
      !company.isActive &&
      pathname !== INACTIVE_COMPANY &&
      pathname !== COMPANY_UPDATE_PAYMENT_METHOD &&
      pathname !== LOGOUT
    ) {
      throw redirect({to: INACTIVE_COMPANY, replace: true});
    }
    if (pathname === LOGOUT) {
      throw redirect({to: LOGOUT, replace: true});
    }
    if (!currentUser || !accessCheck(currentUser)) {
      throw redirect({to: USER_HOME, replace: true});
    }
  };
}
